// ── CallRegister 유틸리티 함수 ───────────────────────────────────────────────
// - 날짜 처리: getKstNow, addKstDays, fmtYmd, isWeekend
// - 그리드 레이아웃: makeFluidTemplate, getColumnMinPx, minWeightFromPx
// - 입력 정규화: sanitizePhoneInput, normalizeAddressForSubmit
// - 주소 지오코딩: geocodeAddressForClient (Nominatim fallback)
// - 데이터 변환: makeEditForm, compareByCallDateDescThenNameAsc, parseNaverPlace

import {
  MIN_COL_PX, MIN_COL_WEIGHT, PHONE_INPUT_MAX_LEN,
  KOREA_LAT_MIN, KOREA_LAT_MAX, KOREA_LON_MIN, KOREA_LON_MAX,
  DEFAULT_FORM, FORM_COLUMNS, LIST_COLUMNS,
} from './constants';
import { toDateString, nowKST } from '../../../utils/date';

// ── 날짜 헬퍼 ────────────────────────────────────────────────────────────────

/** 한국 표준시(KST) 기준 현재 시각을 Date 객체로 반환
 * nowKST: utils/date.js 사용 (new Date + KST 수동 변환 금지) */
export function getKstNow() {
  return nowKST();
}

/** KST 기준 날짜에 n일을 더한 Date 객체 반환 */
export function addKstDays(d, n) {
  // Date 객체 복제 — 유일한 방법, utils 교체 불가
  const x = new Date(d);
  x.setUTCDate(x.getUTCDate() + n);
  return x;
}

/** Date 객체를 "YYYY-MM-DD" 문자열로 변환 */
export function fmtYmd(d) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/** 주어진 날짜가 주말인지 판단 */
export function isWeekend(d) {
  const day = d.getUTCDay();
  return day === 0 || day === 6;
}

// ── 그리드 레이아웃 헬퍼 ─────────────────────────────────────────────────────

/** 컬럼 배열로부터 CSS grid-template-columns 값 생성 (minmax + fr 단위) */
export function makeFluidTemplate(columns) {
  return columns
    .map((col) => {
      const minPx = Math.max(MIN_COL_PX, Number(col?.min_px) || MIN_COL_PX);
      const weight = Math.max(MIN_COL_WEIGHT, Number(col?.w) || MIN_COL_WEIGHT);
      return `minmax(${minPx}px, ${weight}fr)`;
    })
    .join(' ');
}

/** 지정된 컬럼 인덱스의 최소 px (FORM_COLUMNS / LIST_COLUMNS 중 큰 값) */
export function getColumnMinPx(index) {
  const formMin = Number(FORM_COLUMNS[index]?.min_px) || MIN_COL_PX;
  const listMin = Number(LIST_COLUMNS[index]?.min_px) || MIN_COL_PX;
  return Math.max(MIN_COL_PX, formMin, listMin);
}

/** px 기반 최소 너비를 fr 가중치로 환산 */
export function minWeightFromPx(minPx, viewportWidth, totalWeight) {
  const safeViewport = Math.max(320, Number(viewportWidth) || 320);
  const safeTotalWeight = Math.max(MIN_COL_WEIGHT, Number(totalWeight) || MIN_COL_WEIGHT);
  return (Math.max(MIN_COL_PX, Number(minPx) || MIN_COL_PX) / safeViewport) * safeTotalWeight;
}

// ── 입력 정규화 ───────────────────────────────────────────────────────────────

/** 전화번호 입력 정규화: 숫자·하이픈만 허용, 최대 길이 제한 */
export function sanitizePhoneInput(value) {
  return String(value || '')
    .replace(/[^\d-]/g, '')
    .slice(0, PHONE_INPUT_MAX_LEN);
}

/** 주소 문자열을 API 저장용으로 표준화 (공백만 정리 — 지역 강제 접두 없음).
 *  ⚠️ 예전엔 지역 미포함 시 "경기 김포시"를 강제로 붙여 전국 주소가 김포로 오염됐다(2026-07 제거).
 *  좌표 정확성은 서버 지오코딩(지역 검증)이 담당하고, 지오코딩 실패 시 시/도 포함 안내를 준다. */
export function normalizeAddressForSubmit(addressInput) {
  return String(addressInput || '').trim().replace(/\s+/g, ' ');
}

// ── 주소 지오코딩 (클라이언트 Nominatim fallback) ────────────────────────────

/** 주소 텍스트를 Unicode 정규화 + 전각 콤마 치환 */
function normalizeAddressValidationTextForClient(value) {
  return String(value || '')
    .replace(/[，]/g, ',')
    .trim()
    .replace(/\s+/g, ' ');
}

/** 주소에서 괄호·층·호·동 등 상세 정보를 제거 */
function stripAddressDecoratorsForClient(value) {
  return normalizeAddressValidationTextForClient(
    String(value || '')
      .replace(/\([^)]*\)/g, ' ')
      .replace(/(?:\s*,\s*.*)$/g, ' ')
      .replace(/\s*(?:지하\s*)?\d+\s*층(?:\s.*)?$/g, ' ')
      .replace(/\s*\d+\s*호(?:\s.*)?$/g, ' ')
      .replace(/\s*\d+\s*동\s*\d+\s*호(?:\s.*)?$/g, ' ')
      .replace(/\s*\d+\s*동(?:\s.*)?$/g, ' ')
  );
}

/** 지역명 접두어(경기, 경기도, 김포시) 제거 */
function removeLeadingRegionForClient(value) {
  return normalizeAddressValidationTextForClient(
    String(value || '')
      .replace(/^(?:경기|경기도)\s+/g, '')
      .replace(/^김포시\s+/g, '')
  );
}

/** 지오코딩 시도용 후보 주소 문자열 배열 생성 */
function buildAddressValidationCandidatesForClient(addressInput) {
  const normalized = normalizeAddressValidationTextForClient(addressInput);
  if (!normalized) return [];

  const stripped = stripAddressDecoratorsForClient(normalized);
  const reduced = removeLeadingRegionForClient(stripped);

  return Array.from(new Set([
    normalized,
    stripped,
    reduced,
    `대한민국 ${normalized}`,
    `대한민국 ${stripped}`,
    `대한민국 ${reduced}`,
  ].map((v) => normalizeAddressValidationTextForClient(v)).filter(Boolean)));
}

/** 좌표가 대한민국 범위 내인지 검증 */
function isKoreaCoordinateForClient(latitude, longitude) {
  return Number.isFinite(latitude)
    && Number.isFinite(longitude)
    && latitude >= KOREA_LAT_MIN
    && latitude <= KOREA_LAT_MAX
    && longitude >= KOREA_LON_MIN
    && longitude <= KOREA_LON_MAX;
}

/** Nominatim으로 주소 → 좌표 변환 (클라이언트 fallback용)
 *  @returns {{ latitude: number, longitude: number } | null} */
export async function geocodeAddressForClient(addressInput) {
  const candidates = buildAddressValidationCandidatesForClient(addressInput);
  for (const query of candidates) {
    // eslint-disable-next-line no-await-in-loop
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&countrycodes=kr&q=${encodeURIComponent(query)}`
    ).catch(() => null);
    if (!response?.ok) continue;

    // eslint-disable-next-line no-await-in-loop
    const rows = await response.json().catch(() => []);
    const first = Array.isArray(rows) ? rows[0] : null;
    const latitude = Number(first?.lat);
    const longitude = Number(first?.lon);
    if (isKoreaCoordinateForClient(latitude, longitude)) {
      return { latitude, longitude };
    }
  }
  return null;
}

// ── 데이터 변환 헬퍼 ─────────────────────────────────────────────────────────

/** 목록의 한 행(row)으로 수정 폼 초기값 생성 */
export function makeEditForm(row) {
  return {
    ...DEFAULT_FORM,
    ...row,
    phone: sanitizePhoneInput(row?.phone || ''),
    mobile_phone: sanitizePhoneInput(row?.mobile_phone || ''),
  };
}

/** 목록 정렬: 콜일자 최신순 → 동일 날짜 내 이름 오름차순 */
export function compareByCallDateDescThenNameAsc(a, b) {
  const ad = toDateString(a?.call_date || '');
  const bd = toDateString(b?.call_date || '');
  if (ad !== bd) return bd.localeCompare(ad);
  const an = String(a?.user_name || '').trim();
  const bn = String(b?.user_name || '').trim();
  return an.localeCompare(bn, 'ko-KR');
}

// ── 네이버 플레이스 텍스트 파싱 — naverPlaceParser.js로 분리 ──────────────────
export { parseNaverPlace } from './naverPlaceParser';
